import * as THREE from 'three';
import { EAttributeType } from '../../@types/AttributeTypes';
import { ETextureOrigin, IAssetTexture, IProductTexture, ITexture } from '../../@types/ContextProviderRelated';
import { EAssetType, IFilesApiObject } from '../../@types/FilesAPIModels';
import AttributeModel from '../../models/standard/AttributeModel';
import filesApiService from '../filesApiService';
import { parseFilesApiSingleProductRequest } from '../jsonParser';
import { loadMaterialTexture, loadProductMaterialTexture } from '../textureLoaderUtility';

interface TextureInstantiation {
  texture: ITexture | THREE.Texture | null | undefined;
  textureOrigin: ETextureOrigin | undefined;
  neededToFetchTexture: boolean;
  fetchingSuccesful: boolean;
  textureId: string | undefined;
  subType: EAssetType | undefined;
}

// --------------------------------------------------------
// EXISTING HELPERS (For API Response Mapping)
// --------------------------------------------------------

const mapProductAttributesToModels = (list: any[] | undefined): AttributeModel[] => {
  if (!Array.isArray(list)) return [];
  return list.map((attrObj: any) => {
    const { attribute, valueNumber, valueText, valueBool } = attrObj ?? {};
    let value: string | number = '';
    if (valueBool !== null && valueBool !== undefined) value = valueBool ? 'true' : 'false';
    else if (valueNumber !== null && valueNumber !== undefined) value = valueNumber;
    else if (valueText !== null && valueText !== undefined) value = valueText;

    return new AttributeModel(
      Number(attribute?.id ?? 0),
      String(attribute?.key ?? ''),
      value,
      null,
      attribute?.type as EAttributeType | undefined,
      attribute?.unit ?? null,
      false, // visible
      true,
    );
  });
};

const mapProductObjectsForTexture = (objs: any[] | undefined): IFilesApiObject[] => {
  if (!Array.isArray(objs)) return [];
  return objs.map((o: any) => ({
    id: Number(o?.id ?? 0),
    name: String(o?.name ?? ''),
    attributes: mapProductAttributesToModels(o?.attributes),
  }));
};

// --------------------------------------------------------
// NEW HELPERS (For JSON Export Restoration)
// --------------------------------------------------------

/**
 * Restores AttributeModels directly from the exported JSON structure 
 * (which matches AttributeModel properties, not the nested API format).
 */
const restoreAttributesFromExport = (list: any[] | undefined): AttributeModel[] => {
  if (!Array.isArray(list)) return [];
  return list.map(
    (a) =>
      new AttributeModel(
        a.id,
        a.key,
        a.value,
        a.predecessorId,
        a.type,
        a.unit,
        a.hidden,
        a.forced,
        a.ownerId,
        a.userId,
        a.publicFlag,
        a.localization,
        a.defaults,
        a.validation,
        a.unitFamily,
      ),
  );
};

/**
 * Restores IFilesApiObjects (texture objects) from the exported JSON structure.
 */
const restoreObjectsFromExport = (list: any[] | undefined): IFilesApiObject[] => {
  if (!Array.isArray(list)) return [];
  return list.map((o) => ({
    id: o.id,
    name: o.name,
    // Recursively restore attributes using the export-specific helper
    attributes: restoreAttributesFromExport(o.attributes),
    localization: o.localization,
    ifcObject: o.ifcObject,
  }));
};

// --------------------------------------------------------
// MAIN FUNCTION
// --------------------------------------------------------

export async function fetchTextureFromApi(textureData: any, isFilesApiAvailable?: boolean): Promise<TextureInstantiation> {
  const defaultResult: TextureInstantiation = {
    texture: undefined,
    neededToFetchTexture: false,
    fetchingSuccesful: false,
    textureId: textureData?.id !== null ? String(textureData.id) : undefined,
    subType: undefined,
    textureOrigin: textureData?.textureOrigin,
  };
  if (!textureData || isFilesApiAvailable === false) return defaultResult;

  if (textureData.id === -1) {
    return {
      texture: null,
      neededToFetchTexture: false,
      fetchingSuccesful: true,
      textureId: '-1',
      subType: undefined,
      textureOrigin: textureData.textureOrigin,
    };
  }

  // Destructure possible existing data from JSON
  const { textureOrigin, id, assetId, type, name, rotation, offset, color, attributes, objects, country } = textureData as {
    textureOrigin: ETextureOrigin;
    id: number;
    assetId?: number;
    type: EAssetType;
    name?: string;
    rotation?: number;
    offset?: number;
    color?: string;
    attributes?: any[];
    objects?: any[];
    country?: string;
  };

  let fetchingSuccesful = true;

  try {
    let texture: ITexture | undefined;

    if (textureOrigin === ETextureOrigin.ASSET) {
      if (type === EAssetType.MATERIAL) {
        const mat = await loadMaterialTexture(id);
        texture = {
          textureOrigin,
          id,
          name: name ?? '',
          type,
          material: mat,
          color: undefined,
          rotation: rotation ?? 0,
          offset: offset ?? 0,
        } as IAssetTexture;
        if (mat === null) fetchingSuccesful = false;
      } else if (type === EAssetType.COLOR) {
        const c = await filesApiService.getColorById(id);
        texture = {
          textureOrigin,
          id,
          name: name ?? '',
          type,
          material: null,
          color: c?.hex ?? color,
          rotation: rotation ?? 0,
          offset: offset ?? 0,
        } as IAssetTexture;
        if (!c) fetchingSuccesful = false;
      }
    } else if (textureOrigin === ETextureOrigin.PRODUCT) {
      if (!assetId) return defaultResult;

      let texAttributes: AttributeModel[] = [];
      let texObjects: IFilesApiObject[] = [];

      // ------------------------------------------------------
      // CHECK IF WE HAVE SAVED DATA (JSON) VS FETCHING (API)
      // ------------------------------------------------------
      const hasSavedAttributes = Array.isArray(attributes) && attributes.length > 0;
      const hasSavedObjects = Array.isArray(objects) && objects.length > 0;

      // Logic: If we have saved data, use it. ONLY fetch if data is missing.
      // This satisfies the "NEVER refetched" requirement for saved models.
      if (hasSavedAttributes || hasSavedObjects) {
        texAttributes = restoreAttributesFromExport(attributes);
        texObjects = restoreObjectsFromExport(objects);
      } else {
        // Fallback: Fetch from API if clean import (no saved data)
        try {
          const raw = await filesApiService.dataRequest(`product/${id}`);
          const parsed = parseFilesApiSingleProductRequest(raw);
          texAttributes = mapProductAttributesToModels(parsed?.attributes);
          texObjects = mapProductObjectsForTexture(parsed?.objects);
        } catch {
          // optional; continue without meta
          console.warn('Failed to fetch product metadata for texture', id);
        }
      }

      if (type === EAssetType.MATERIAL) {
        const mat = await loadProductMaterialTexture(id, assetId);
        texture = {
          textureOrigin,
          id,
          assetId,
          name: name ?? '',
          type,
          material: mat,
          color: undefined,
          rotation: rotation ?? 0,
          offset: offset ?? 0,
          attributes: texAttributes,
          objects: texObjects,
          country,
        } as IProductTexture;
        if (mat === null) fetchingSuccesful = false;
      } else if (type === EAssetType.COLOR) {
        const c = await filesApiService.getProductColorById(id, assetId);
        texture = {
          textureOrigin,
          id,
          assetId,
          name: name ?? '',
          type,
          material: null,
          color: c?.hex ?? color,
          rotation: rotation ?? 0,
          offset: offset ?? 0,
          attributes: texAttributes,
          objects: texObjects,
          country,
        } as IProductTexture;
        if (!c) fetchingSuccesful = false;
      }
    }

    if (texture) {
      return {
        texture,
        neededToFetchTexture: true,
        fetchingSuccesful,
        textureId: String(textureData.id),
        subType: type,
        textureOrigin,
      };
    }

    return { ...defaultResult, neededToFetchTexture: true, fetchingSuccesful: false, subType: type };
  } catch (error) {
    console.error('Error fetching texture with id', textureData?.id, error);
    return { ...defaultResult, neededToFetchTexture: true, fetchingSuccesful: false, subType: type };
  }
}